import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LoadingContainer } from './ui/Spinner';
import { formatCurrency } from '../utils/formatting';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const AggregationTable = ({ data, groupBy, nestedBy }) => {
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Helper function to format the period label
    const formatPeriod = (period) => {
        if (groupBy === 'week') {
            return new Date(period).toLocaleDateString();
        } else if (groupBy === 'month') {
            // Parse YYYY-MM format
            const [year, month] = period.split('-');
            return new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
        } else if (groupBy === 'year') {
            return period;
        }
        return period;
    };

    const toggleRow = (rowId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }
        setExpandedRows(newExpanded);
    };

    const renderRow = (row, isChild = false) => {
        const isExpanded = expandedRows.has(row.id);
        const hasChildren = row.children && row.children.length > 0;

        return (
            <React.Fragment key={row.id}>
                <tr className={`border-b border-gray-200 dark:border-gray-700 ${isChild ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-800'} hover:bg-gray-50 dark:hover:bg-gray-700`}>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center">
                            {!isChild && hasChildren && (
                                <button
                                    onClick={() => toggleRow(row.id)}
                                    className="mr-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                >
                                    {isExpanded ? (
                                        <ChevronDownIcon className="h-4 w-4" />
                                    ) : (
                                        <ChevronRightIcon className="h-4 w-4" />
                                    )}
                                </button>
                            )}
                            {isChild && <div className="w-6 ml-2" />}
                            {formatPeriod(row.period)}
                        </div>
                    </td>
                    <td className="px-4 py-3">
                        {row.session_count}
                    </td>
                    <td className="px-4 py-3">
                        {row.total_hours}
                    </td>
                    <td className={`px-4 py-3 font-medium ${row.total_profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(row.total_profit)}
                    </td>
                    <td className="px-4 py-3 text-green-600 dark:text-green-400">
                        {formatCurrency(row.total_rakeback)}
                    </td>
                    <td className={`px-4 py-3 font-medium ${row.hourly_rate >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(row.hourly_rate)}
                    </td>
                    {groupBy === 'stake' && (
                        <td className={`px-4 py-3 font-medium ${(row.bb_per_hour || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {row.bb_per_hour !== undefined ? `${row.bb_per_hour} BB/hr` : '-'}
                        </td>
                    )}
                </tr>
                {hasChildren && isExpanded && row.children.map(child => renderRow(child, true))}
            </React.Fragment>
        );
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th scope="col" className="px-4 py-3">{groupBy === 'week' ? 'Week Starting' : groupBy === 'month' ? 'Month' : groupBy === 'year' ? 'Year' : groupBy === 'location' ? 'Location' : 'Stake'}</th>
                        <th scope="col" className="px-4 py-3">Sessions</th>
                        <th scope="col" className="px-4 py-3">Hours</th>
                        <th scope="col" className="px-4 py-3">Profit</th>
                        <th scope="col" className="px-4 py-3">Rakeback</th>
                        <th scope="col" className="px-4 py-3">$/hr</th>
                        {groupBy === 'stake' && <th scope="col" className="px-4 py-3">BB/hr</th>}
                    </tr>
                </thead>
                <tbody>
                    {data.map(row => renderRow(row))}
                </tbody>
            </table>
        </div>
    );
};

function SessionAggregations({ className = '' }) {
    const [groupBy, setGroupBy] = useState('week');
    const [nestedBy, setNestedBy] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get('/api/session-aggregations/', {
                    params: { 
                        group_by: groupBy,
                        nested_by: nestedBy
                    }
                });
                setData(response.data);
            } catch (err) {
                setError('Failed to fetch aggregation data');
                console.error('Error fetching aggregations:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [groupBy, nestedBy]);

    const handleGroupChange = (newGroupBy) => {
        setGroupBy(newGroupBy);
        // Reset nested view when changing main grouping
        setNestedBy(null);
    };

    const handleNestedViewClick = () => {
        if (groupBy === 'location') {
            setNestedBy(nestedBy === 'stake' ? null : 'stake');
        } else if (groupBy === 'stake') {
            setNestedBy(nestedBy === 'location' ? null : 'location');
        }
    };

    return (
        <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm ${className}`}>
            <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Session Statistics</h2>
                    <div className="flex flex-wrap gap-2">
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleGroupChange('week')}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                    groupBy === 'week'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                By Week
                            </button>
                            <button
                                onClick={() => handleGroupChange('month')}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                    groupBy === 'month'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                By Month
                            </button>
                            <button
                                onClick={() => handleGroupChange('year')}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                    groupBy === 'year'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                By Year
                            </button>
                            <button
                                onClick={() => handleGroupChange('location')}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                    groupBy === 'location'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                By Location
                            </button>
                            <button
                                onClick={() => handleGroupChange('stake')}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                    groupBy === 'stake'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                By Stake
                            </button>
                        </div>
                        {(groupBy === 'location' || groupBy === 'stake') && (
                            <button
                                onClick={handleNestedViewClick}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                    nestedBy
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                {nestedBy ? 'Hide Details' : `Show by ${groupBy === 'location' ? 'Stakes' : 'Locations'}`}
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <LoadingContainer />
                ) : error ? (
                    <div className="p-4 text-red-600 dark:text-red-400">{error}</div>
                ) : (
                    <AggregationTable data={data} groupBy={groupBy} nestedBy={nestedBy} />
                )}
            </div>
        </div>
    );
}

export default SessionAggregations;